/**
 * A TRILHA — por onde a pessoa passou, para o "Voltar" saber o que fazer.
 *
 * POR QUE ISTO EXISTE. Até 31/08/2026 o "Voltar" mentia por desenho: 19 telas
 * tinham o botão, só 4 usavam `navigate(-1)`, e o único caminho de volta
 * inteligente era o `voltarPara` de `pages/ordens-servico/form.tsx` — que SÓ a
 * Agenda preenchia. Abrir uma OS pela Inbox e clicar em Voltar devolvia a pessoa
 * para a lista de OS, um lugar onde ela nunca esteve.
 *
 * ⛔ TRILHA NÃO É BREADCRUMB, e confundir os dois é o erro clássico.
 *
 *   - O BREADCRUMB é a ÁRVORE: `Ordens de Serviço › OS 123`. É sempre igual,
 *     tenha a pessoa vindo da Inbox, da Agenda ou de um link colado no zap.
 *     Ele responde "onde isto MORA".
 *   - A TRILHA é o CAMINHO ANDADO. Muda a cada visita. Responde "de onde EU
 *     vim". É o que o botão Voltar precisa, e só ele.
 *
 * Breadcrumb que mostra o caminho andado é defeito conhecido: a mesma tela
 * exibe uma hierarquia diferente a cada visita, e ele vira uma cópia pior do
 * botão do navegador.
 *
 * ONDE ISTO VIVE: `sessionStorage`, não estado do React. Precisa sobreviver ao
 * F5 (recarregar não apaga de onde você veio) e precisa morrer quando a aba
 * fecha (amanhã, numa aba nova, não há trilha nenhuma). É exatamente o contrato
 * do `sessionStorage` — `localStorage` guardaria demais, `useState` de menos.
 *
 * QUEM REGISTRA: a própria `CustomMolduraPagina`, ao montar. Toda tela que usa a
 * moldura entra na trilha sem escrever uma linha; tela que não usa fica de fora
 * — e ficar de fora é o comportamento certo, não um bug.
 */

const CHAVE = 'casaa-trilha';

/** Um degrau: onde a pessoa esteve e como aquela tela se chamava. */
export type Degrau = {
  /** `location.pathname` — para onde o Voltar navega. */
  caminho: string;
  /** O título da tela, que vira o RÓTULO do botão: "← Inbox". */
  titulo: string;
};

/** Quantos degraus se guarda. Passou disso, o mais antigo cai.
 *
 *  Por que 10 e não infinito: a trilha só alimenta o botão Voltar, que olha
 *  UM degrau atrás. O resto é margem para o caso de voltar várias vezes
 *  seguidas. Guardar tudo faria o `sessionStorage` crescer numa sessão longa
 *  sem que ninguém jamais lesse o fundo da pilha. */
const LIMITE = 10;

function ler(): Degrau[] {
  try {
    const cru = sessionStorage.getItem(CHAVE);
    return cru ? (JSON.parse(cru) as Degrau[]) : [];
  } catch {
    /* Aba anônima, storage bloqueado, JSON corrompido por uma versão antiga:
       em todos os casos a resposta certa é "não há trilha", nunca quebrar a
       tela por causa do rótulo de um botão. */
    return [];
  }
}

function gravar(degraus: Degrau[]): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(degraus.slice(-LIMITE)));
  } catch {
    /* Silêncio de propósito — ver acima. */
  }
}

/**
 * Registra a tela atual como o degrau mais recente.
 *
 * ⚠️ RECARREGAR A MESMA TELA NÃO EMPILHA. Sem esta guarda, um F5 poria a tela
 * atual como seu próprio degrau anterior, e o Voltar passaria a apontar para
 * onde a pessoa já está — o botão fica na tela, clica, e nada acontece.
 */
export function registrarNaTrilha(degrau: Degrau): void {
  const degraus = ler();
  const ultimo = degraus[degraus.length - 1];
  if (ultimo?.caminho === degrau.caminho) {
    /* Mesma tela: não empilha, mas ATUALIZA o título — uma OS que acabou de
       carregar troca "Carregando…" pelo número dela. */
    degraus[degraus.length - 1] = degrau;
    gravar(degraus);
    return;
  }
  gravar([...degraus, degrau]);
}

/**
 * O degrau anterior ao atual — o destino do botão Voltar.
 *
 * Devolve `null` quando a pessoa entrou direto nesta tela (link do zap,
 * favorito, primeira tela da sessão). Nesse caso o Voltar não aparece: um botão
 * "Voltar" que leva a um lugar arbitrário é pior do que botão nenhum.
 */
export function degrauAnterior(caminhoAtual: string): Degrau | null {
  const degraus = ler();
  for (let i = degraus.length - 1; i >= 0; i--) {
    if (degraus[i].caminho !== caminhoAtual) return degraus[i];
  }
  return null;
}

/** Consome o degrau anterior: devolve para onde ir e tira os degraus andados.
 *
 *  Sem isto, voltar duas vezes seguidas ficaria preso no mesmo degrau — a
 *  trilha continuaria dizendo que a tela anterior é a mesma. */
export function voltarNaTrilha(caminhoAtual: string): string | null {
  const degraus = ler();
  for (let i = degraus.length - 1; i >= 0; i--) {
    if (degraus[i].caminho !== caminhoAtual) {
      gravar(degraus.slice(0, i));
      return degraus[i].caminho;
    }
  }
  return null;
}

/** Apaga a trilha. Serve ao logout e à bancada de teste. */
export function limparTrilha(): void {
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    /* ver acima */
  }
}
